import _ from 'lodash';

import SoqlDataProvider from 'common/visualizations/dataProviders/SoqlDataProvider';
import { type SoqlDataProviderConfig } from 'common/visualizations/dataProviders/types';
import { select as soqlSelect, f, composeQuery, ref } from 'common/soql_builder';
import { SoQLType } from 'common/types/soql';

class GeospaceDataProvider extends SoqlDataProvider {
  constructor(config: SoqlDataProviderConfig, useCache = false) {
    super(config);

    if (useCache) {
      const cached = this.cachedInstance('GeospaceDataProvider');
      if (cached) {
        return cached as GeospaceDataProvider;
      }
    }
  }

  /**
   * Get the geographic bounds for a dataset, used primarily for centering a new map
   * on the rows in the data source.
   */
  getFeatureExtent(
    columnName: string,
    columnDataType?: SoQLType,
    ignoreInvalidLatLng?: boolean,
    whereClauseComponent?: string,
  ) {
    const EXTENT_NAME = 'extent_' + columnName;
    const query = (fieldName: string) => {
      // The `extent` soql function does not take "location" type columns, so
      // we cast them to "point"
      const castColumn =
        columnDataType === SoQLType.SoQLLocationT
          ? f.cast(SoQLType.SoQLPointT)(ref(fieldName))
          : ref(fieldName);
      return composeQuery({
        selects: [soqlSelect(f.extent(castColumn), EXTENT_NAME)],
        where: ignoreInvalidLatLng ? f.withinBox(ref(fieldName), -90, -180, 90, 180) : undefined,
        legacyWhereClause: whereClauseComponent,
      });
    };

    return super.invokeSoqlQuery(query, [columnName]).then((response) => {
      const coordinates = _.get(response, `[0].${EXTENT_NAME}.coordinates[0][0]`);

      if (!_.isUndefined(coordinates)) {
        return {
          southwest: [coordinates[0][1] as number, coordinates[0][0] as number],
          northeast: [coordinates[2][1] as number, coordinates[2][0] as number],
        };
      }
    });
  }
}

export default GeospaceDataProvider;
